import { useEffect, useLayoutEffect, useRef, useState } from 'react'

/**
 * Den Logo-Screen beim Start stehen lassen, bis der Inhalt WIRKLICH steht.
 *
 * `ProtectedRoute` zeigt das Logo, solange die Anmeldung lädt — und gibt die
 * Seite danach frei. Was dann noch kommt (Tabs wiederherstellen, das Projekt
 * aus Dexie holen, den gemerkten Canvas-Zustand anwenden), passiert vor den
 * Augen des Benutzers: Die Aufgabenliste steht kurz mit offener Seitenleiste
 * da, dann schneidet es auf den maximierten Canvas um.
 *
 * Hier meldet eine Seite **Aufschub** an und gibt ihn frei, sobald sie steht.
 *
 * ## Die Regel, wer anmeldet
 *
 * > **Wer die Fenstergeometrie aus gemerktem Zustand umbaut, meldet Aufschub
 * > an.**
 *
 * Heute ist das genau eine Seite: `ProjectPage` mit ihrem `canvasPanelState`.
 * Dort springt beim Laden die ganze Geometrie — Seitenleiste weg, Tabs rücken,
 * Aufgabenliste fährt aus. Wo sich bloß eine Liste füllt (Kurz notiert,
 * Demnächst), springt nichts, und Warten verzögerte den Start ohne Gegenwert.
 *
 * **Der Vorgabewert ist deshalb „nicht warten".** Bewusst so herum: Müsste
 * jede Seite melden, dass sie steht, wären es ~17 Aufrufstellen (acht Routen in
 * `ElectronRoutes`, davon nur `/home/*` mit dem Tab-Stapel und seinen zehn
 * Seiten) — und jede vergessene kostete eine volle Sekunde beim Setup-Flow,
 * beim Login, im Task-Detail-Fenster.
 *
 * ## Einmal je Fensterleben
 *
 * Nach der ersten Freigabe wird nie wieder aufgeschoben. Sonst legte sich das
 * Logo mitten im Betrieb über die App, sobald man auf ein Projekt wechselt.
 *
 * ## Modulzustand, kein Context
 *
 * Anmelder und Abfrager stehen in verschiedenen Teilbäumen, und es geht um
 * genau einen Vorgang. Ein Context wäre Apparat — und brächte ein Wettrennen
 * zurück, das der Modulzustand gar nicht erst zulässt: Meldet die Seite den
 * Aufschub, bevor `ProtectedRoute` nachfragt, steht er schon da.
 */

/**
 * Wie lange ein Aufschub höchstens gilt.
 *
 * Er MUSS eine Obergrenze haben: Ein gelöschtes Projekt ist von „lädt noch"
 * nicht unterscheidbar — Dexie liefert für beides `undefined`, siehe den
 * Kommentar an der leeren Hülle in `ProjectPage`. Ohne die Uhr stünde man vor
 * einem Logo, das nie weicht.
 *
 * Großzügig gegriffen, weil die beiden Fehler ungleich schwer sind: Zu knapp
 * trifft den Normalfall auf langsamer Hardware und beim Kaltstart — dann ist
 * das Flackern zurück. Zu großzügig trifft nur den kaputten Fall, und dort
 * steht das Logo einmalig länger.
 */
export const REVEAL_HOLD_TIMEOUT_MS = 1000


/**
 * Wie viele Aufschübe gerade laufen.
 *
 * Ein ZÄHLER und kein Boolean, und das ist die Lehre aus dem fünften
 * Praxistest: Zwischen „`ProtectedRoute` rendert die App" und „`ProjectPage`
 * ist gemountet und meldet Aufschub an" liegen mehrere Commits — der Tab-Stapel
 * stellt seine Tabs erst wieder her und weckt sie gestaffelt. In dieser Lücke
 * stand der Vorhang nicht, und man sah Seitenleiste und Tabs.
 *
 * Mit einem Zähler kann `ProtectedRoute` die Lücke selbst überbrücken: Es hält
 * beim ersten Rendern der App kurz mit, bis die Seite darunter übernommen hat.
 * Zwei Aufschübe überlappen, der Vorhang fällt erst, wenn beide durch sind.
 */
let holds = 0
/** Ist der Vorgang für dieses Fensterleben durch? Dann nie wieder aufschieben. */
let done = false
/** Wer beim Wechsel benachrichtigt werden will (`useWindowRevealOverlay`). */
const listeners = new Set<() => void>()

function emit() {
  listeners.forEach(listener => listener())
}

function acquire(): boolean {
  if (done) return false
  holds += 1
  if (holds === 1) emit()
  return true
}

/**
 * Warum ein Aufschub endet — und daran hängt, ob je wieder einer möglich ist.
 *
 * - `ready` — die Seite steht (oder die Obergrenze war erreicht). Fällt damit
 *   der letzte Aufschub, ist der Vorgang für dieses Fensterleben durch.
 * - `gone` — der Anmelder ist verschwunden, bevor er fertig war. Der Vorgang
 *   ist NICHT durch: Wer als Nächstes aufschieben will, darf.
 */
type ReleaseReason = 'ready' | 'gone'

function release(reason: ReleaseReason = 'ready') {
  if (holds === 0) return
  holds -= 1
  if (holds > 0) return
  if (reason === 'ready') done = true
  emit()
}

/** Alle Aufschübe auf einen Schlag beenden — die Obergrenze. */
function releaseAll() {
  if (holds === 0) return
  holds = 0
  done = true
  emit()
}

/** Nur für Tests — den Modulzustand zurücksetzen. */
export function __resetWindowRevealHold() {
  holds = 0
  done = false
  listeners.clear()
}

/**
 * Aufschub anmelden, solange `pending` gilt, und mit dem Umschlag auf `false`
 * freigeben.
 *
 * Angemeldet wird nur, solange der Vorgang nicht durch ist: Ein Aufruf, der
 * `pending: false` beginnt, schiebt nichts auf und schließt auch nichts —
 * sonst nähme eine Seite, die zufällig zuerst rendert, einer anderen den
 * Aufschub weg.
 *
 * @example
 * // ProjectPage: aufschieben, bis der gemerkte Canvas-Zustand dasteht
 * useWindowRevealHold(isActive && !!projectId && !canvasGeometrySettled)
 */
export function useWindowRevealHold(pending: boolean) {
  const acquiredRef = useRef(false)

  /*
    LAYOUT-Effekt beim Anmelden, und das schließt ein Bild.

    Ein passiver Effekt läuft erst, NACHDEM der Browser gezeichnet hat. Der
    Ablauf wäre: `ProtectedRoute` rendert die Seite ohne Vorhang → gemalt →
    diese Zeile meldet an → Vorhang erscheint. Das eine gemalte Bild ist genau
    der halbfertige Anblick, den der Vorhang verbergen soll.
  */
  useLayoutEffect(() => {
    if (pending) {
      if (!acquiredRef.current) acquiredRef.current = acquire()
      return
    }
    if (!acquiredRef.current) return

    /*
      EIN BILD Abstand vor der Freigabe.

      `pending` fällt in dem Commit, in dem die Bedingung erfüllt ist. Gezeichnet
      ist dieser Commit dann aber noch nicht, und an ihm hängen weitere: Das
      Panel meldet an den `CanvasFocusContext`, `MainLayout` leitet per
      Layout-Effekt an die Titelleiste weiter. Gäbe der Vorhang sofort frei,
      verschwände er, bevor irgendetwas davon auf dem Schirm steht.
    */
    const frame = requestAnimationFrame(() => {
      acquiredRef.current = false
      release('ready')
    })
    return () => cancelAnimationFrame(frame)
  }, [pending])

  /*
    Ein Unmount mitten im Aufschub — Projektwechsel, Tab zu — darf ihn nicht
    stehen lassen. Sonst liefe er in die Obergrenze und der Vorhang bliebe eine
    Sekunde über einer Seite, die längst steht.

    `'gone'` und NICHT `'ready'`: `React.StrictMode` (main.tsx) mountet doppelt —
    mounten, aufräumen, wieder mounten. Räumte das Aufräumen den Vorgang als
    ERLEDIGT ab, könnte der zweite Mount nie wieder aufschieben.
  */
  useEffect(() => () => {
    if (acquiredRef.current) {
      acquiredRef.current = false
      release('gone')
    }
  }, [])
}

/**
 * Die Lücke zwischen „die App wird gerendert" und „die Seite darunter hat
 * übernommen" überbrücken.
 *
 * `ProtectedRoute` ruft das beim ersten Rendern der App auf. Ohne diesen
 * Überbrücker klaffte dort ein Loch: Der Tab-Stapel stellt seine Tabs erst
 * wieder her und weckt sie gestaffelt, `ProjectPage` meldet also erst mehrere
 * Commits später Aufschub an — und bis dahin standen Seitenleiste und Tabs
 * ungeschützt im Bild.
 *
 * Zwei Bilder, nicht eines: Das erste deckt den Commit ab, in dem die App
 * erscheint, das zweite gibt den Seiten darunter Zeit, selbst anzumelden. Wer
 * dann nichts angemeldet hat, brauchte auch nichts — der Vorhang fällt.
 */
export function useWindowRevealHandoff() {
  const acquiredRef = useRef(false)

  useLayoutEffect(() => {
    acquiredRef.current = acquire()
    if (!acquiredRef.current) return

    let second = 0
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => {
        if (!acquiredRef.current) return
        acquiredRef.current = false
        release('ready')
      })
    })
    return () => {
      cancelAnimationFrame(first)
      cancelAnimationFrame(second)
      if (acquiredRef.current) {
        acquiredRef.current = false
        release('gone')
      }
    }
  }, [])
}

/**
 * Ob der Vorhang über der App liegen soll.
 *
 * **Kein Ausblenden.** Ein durchsichtig werdender Schirm ist ein FENSTER auf
 * alles, was darunter noch nicht steht — er zeigte neun Bilder lang die
 * Seitenleiste. Ein Vorhang, der etwas verbergen soll, darf nicht durchsichtig
 * werden, solange es etwas zu verbergen gibt. Der harte Schnitt kostet nichts:
 * Was danach steht, ist fertig.
 *
 * Bleibt ein Zeitgeber: die **Obergrenze**.
 */
export function useWindowRevealOverlay(): boolean {
  const [held, setHeld] = useState(holds > 0)

  // Am Modulzustand hängen. Ein Anmelder kann VOR dieser Komponente gelaufen
  // sein, deshalb wird beim Anmelden gleich der aktuelle Stand mitgelesen.
  // LAYOUT-Effekt, damit der Vorhang im Commit erscheint, nicht ein Bild später.
  useLayoutEffect(() => {
    const sync = () => setHeld(holds > 0)
    listeners.add(sync)
    sync()
    return () => { listeners.delete(sync) }
  }, [])

  useEffect(() => {
    if (!held) return
    const timer = window.setTimeout(releaseAll, REVEAL_HOLD_TIMEOUT_MS)
    return () => window.clearTimeout(timer)
  }, [held])

  return held
}
